import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { LuTriangleAlert } from "react-icons/lu";
import API from "../../api";
import { capture } from "../../instrument";
import toast from "react-hot-toast";
import { ADMISSION_STATUS, ADMISSION_STATUS_REASON } from "../../utils";

const BatchRejectModal = ({ selectedAdmissions, setSelectedAdmissions, setShowBatchRejectModal, getAdmissions, setLoading, loading }) => {
  const [reason, setReason] = useState("");

  const batchRejectAdmissions = async () => {
    setLoading(true);
    const selectedAdmissionIds = Array.from(selectedAdmissions);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const admissionId of selectedAdmissionIds) {
        try {
          const formData = new FormData();
          formData.append("data", JSON.stringify({ status: ADMISSION_STATUS.ARCHIVED, statusReason: ADMISSION_STATUS_REASON.REJECTED, rejectReason: reason }));
          const { ok } = await API.putFormData(`/admin/admission/${admissionId}`, formData);
          if (ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          capture(error);
          console.log(`Error rejecting admission ${admissionId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} dossier${successCount > 1 ? "s" : ""} refusé${successCount > 1 ? "s" : ""} avec succès`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} dossier${errorCount > 1 ? "s" : ""} n'${errorCount > 1 ? "ont" : "a"} pas pu être refusé${errorCount > 1 ? "s" : ""}`);
      }

      setSelectedAdmissions(new Set());
      setShowBatchRejectModal(false);
      setReason("");
      getAdmissions();
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors du refus des dossiers");
    }
    setLoading(false);
  };

  useHotkeys("mod+enter", batchRejectAdmissions, { enabled: !!reason.trim() && !loading, enableOnFormTags: true, preventDefault: true }, [batchRejectAdmissions, reason, loading]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[220] flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-red-100 rounded-full p-2">
            <HiOutlineExclamationCircle className="h-6 w-6 text-red-500" />
          </div>
          <h2 className="text-xl font-bold">Refuser les dossiers sélectionnés</h2>
        </div>

        <div className="mb-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
            <div className="flex items-center gap-2">
              <LuTriangleAlert className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                Vous êtes sur le point de refuser <span className="font-semibold">{selectedAdmissions.size}</span> dossier{selectedAdmissions.size > 1 ? "s" : ""}. Cette action est
                irréversible.
              </span>
            </div>
          </div>

          <label htmlFor="batchRejectReason" className="block text-sm font-medium text-gray-700 mb-2">
            Motif du refus (appliqué à tous les dossiers)
          </label>
          <div className="relative">
            <textarea
              id="batchRejectReason"
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Veuillez indiquer le motif du refus pour tous les dossiers sélectionnés..."
            />
            {reason && <div className="absolute left-2 -top-2 bg-white px-1 text-xs text-gray-500 pointer-events-none">Motif du refus</div>}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" disabled={loading} onClick={() => setShowBatchRejectModal(false)}>
            Annuler
          </button>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={!reason.trim() || loading}
            onClick={batchRejectAdmissions}
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Refus en cours...
              </>
            ) : (
              `Refuser ${selectedAdmissions.size} dossier${selectedAdmissions.size > 1 ? "s" : ""}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchRejectModal;
